import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { PropTypes } from "prop-types";
import React from "react";
import autoBind from "react-autobind";
import { OverlayTrigger } from "react-bootstrap";
import Collapse from "react-bootstrap/Collapse";
import Tooltipable from "~/components/effects/tooltipable";
import DateTimePicker from "~/components/forms/DateTimePicker";
import AsyncSelect from "~/components/forms/dropdowns/SelectWrapper/AsyncSelect";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import StatusSelect from "~/components/forms/dropdowns/StatusSelect";
import { TeammatePicker } from "~/components/forms/dropdowns/TeamMemberSelect";
import TaskEditor from "~/components/forms/HtmlEditors/TaskEditor";
import VariableMenuButton from "~/components/forms/VariableMenuButton";
import InsertDocumentButton from "~/components/insert_document_button";
import { CALL_DISPOSITIONS } from "~/constants/dropdowns/CallDispositions";
import { RATING_OPTIONS } from "~/constants/dropdowns/leads/RatingOptions";
import { TASK_CATEGORY_OPTIONS } from "~/constants/dropdowns/TaskCategoryOptions";
import { TASK_PRIORITY_OPTIONS } from "~/constants/dropdowns/TaskPriorityOptions";
import { TASK_STATUS_OPTIONS } from "~/constants/dropdowns/TaskStatusOptions";
import LeadHelpers from "~/helpers/lead_helpers";
import { isAdminOrStaff } from "~/helpers/userRoleHelper";
import dayjs from "~/lib/dayjs";
import ScheduleButton from "../ScheduleForm/ScheduleButton";

const emailTooltip =
  "Open and click tracking will not work since we are sending this through your Google/Microsoft account which does not support it like our email server used for bulk/blast/campaign emails.";
const noop = () => undefined;

class FormFooterView extends React.Component {
  constructor(props) {
    super(props);

    autoBind(this);

    this.state = {
      isOpen: false,
      linksToInsert: [],
    };

    this.containerRef = React.createRef();
    this.taskNameRef = React.createRef();
  }

  onCollapseClick(e) {
    e.preventDefault();

    const { isOpen } = this.state;
    const { setTaskField } = this.props;

    this.setState({ isOpen: !isOpen });
    setTaskField("isOpen", !isOpen);
  }

  renderTasksForm = () => {
    const { errors, task, setTaskField } = this.props;

    const { isOpen, linksToInsert } = this.state;
    const variablesEnabled = isAdminOrStaff();

    return (
      <>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="task_name">Task Name</label>
            <div className="input-group">
              <input
                ref={this.taskNameRef}
                id="task_name"
                name="name"
                value={task.name}
                className={classNames(
                  "d-block form-control",
                  errors.name ? "has-error" : "",
                )}
                placeholder="Enter Name"
                onChange={(val) =>
                  setTaskField("name", val && val.target.value)
                }
              />
              {variablesEnabled && (
                <div className="input-group-append">
                  <VariableMenuButton
                    inputRef={this.taskNameRef}
                    value={task.name}
                    onChange={(value) => setTaskField("name", value)}
                    includeAlertVariables
                  />
                </div>
              )}
            </div>
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="task-category">Task Category</label>
            <Select
              id="task-category"
              name="category"
              options={TASK_CATEGORY_OPTIONS}
              placeholder="Select Category"
              value={task.category}
              isInvalid={!!errors.category}
              onChange={(val) => setTaskField("category", val?.value || "")}
              isSearchable
            />
          </div>
        </div>

        <div className="form-group mb15">
          <label htmlFor="task_description" className="d-block">
            Description
          </label>
          <TaskEditor
            className={errors.description ? "tiny-mce has-error" : ""}
            onChange={(html) => setTaskField("description", html)}
            placeholder="Enter Description"
            id="task_description"
            leadId={this.props.lead?.id}
            spellCheck={isOpen}
            enableVariables={variablesEnabled}
            includeOwnerVariables
            includeAlertVariables
            linksToInsert={linksToInsert}
            clearLinksToInsert={() => {
              this.setState({ linksToInsert: [] });
            }}
          />
        </div>

        <div className="form-row">
          <div className="form-group col-md-4">
            <InsertDocumentButton
              setLinksToInsert={(links) =>
                this.setState({ linksToInsert: links })
              }
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-12">
            <label htmlFor="leads-tasks-select" className="d-block">
              Related Contacts
            </label>
            <AsyncSelect
              id="leads-tasks-select"
              placeholder="Please Select Contacts ..."
              resourcePath="leads"
              formatOption={(lead) => ({
                value: lead.id,
                label: lead.name,
              })}
              isMulti
              isSearchable
              isInvalid={!!errors.lead_ids}
              value={task.lead_ids}
              onChange={(values) =>
                setTaskField(
                  "lead_ids",
                  values ? values.map((item) => item.value) : [],
                )
              }
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-4">
            <label htmlFor="task_owner_id" className="form-control-label">
              Owner
            </label>
            <TeammatePicker
              id="task_owner_id"
              name="owner_id"
              placeholder="Owner"
              value={task.owner_id}
              onChange={(opt) => setTaskField("owner_id", opt && opt.value)}
              isClearable
            />
          </div>

          <div className="form-group col-md-4">
            <label htmlFor="task-priority">Priority</label>
            <Select
              id="task-priority"
              name="priority"
              placeholder="Select Priority"
              value={task.priority}
              options={TASK_PRIORITY_OPTIONS}
              isSearchable
              isInvalid={!!errors.priority}
              onChange={(val) => setTaskField("priority", val?.value || "")}
            />
          </div>

          <div className="form-group col-md-4">
            <label htmlFor="task-status">Status</label>
            <Select
              id="task-status"
              name="status"
              placeholder="Select Status"
              value={task.status}
              options={TASK_STATUS_OPTIONS}
              isSearchable
              isInvalid={!!errors.status}
              onChange={(val) => setTaskField("status", val?.value || "")}
            />
          </div>
        </div>
      </>
    );
  };

  renderTasksControls = () => {
    const { isOpen } = this.state;
    const { errors, task, setTaskField } = this.props;

    return (
      <>
        <div className="input-group">
          <div className="input-group-prepend">
            <button
              type="button"
              className="btn btn-outline-info"
              onClick={this.onCollapseClick}
            >
              {isOpen ? (
                <FontAwesomeIcon icon={["far", "fa-minus"]} />
              ) : (
                <FontAwesomeIcon icon={["far", "fa-plus"]} />
              )}
            </button>
          </div>
          <DateTimePicker
            placeholder="Follow Up Task"
            format="LL"
            pickerType="date"
            minDate={dayjs().startOf("day").toDate()}
            value={dayjs(task.due_date_at)?.format("LL")}
            isInvalid={errors.due_date_at}
            onChange={(picker) =>
              setTaskField("due_date_at", picker && picker.toDate())
            }
          />
        </div>
        <p className="pt3">Select date for Follow Up</p>
      </>
    );
  };

  render() {
    const { isOpen } = this.state;
    const { showScheduleButton, setSchedule } = this.props;
    const {
      lead,
      onRatingChange,
      rating,
      internal,
      status,
      call_disposition,
      errors,
      showRatingSelect,
      sending,
      submitButtonText,
      submitButtonSendingText,
      onSubmit,
      disabledButton,
      activityType,
      onCallDispositionChange,
      onStatusChange,
      onToggleSharedType,
      submitActionsComponent,
      schedule,
      scheduleBtnPopoverPlacement,
      isEditingMode,
      showPopover,
      renderPopover,
    } = this.props;

    const product = GlobalContainer.product();
    const showTaskSelect = !(
      product === "recruiting" && status === "onboarding"
    ); // show anytime it's not recruiting and Signed (onboardind) status
    const stage = LeadHelpers.determineStatusStage(lead);
    const isAdminOrStaff = Rails.abilities.manageLead;

    return (
      <div>
        {!isEditingMode && (
          <>
            <div className="row m0">
              <div className="col-lg-4 pl0 pr0 mb10-md-down">
                {activityType === "phone_call" && (
                  <div className="mb-2">
                    <Select
                      id="call_disposition"
                      name="call_disposition"
                      placeholder="Call Disposition"
                      value={call_disposition}
                      onChange={onCallDispositionChange}
                      options={CALL_DISPOSITIONS}
                      isInvalid={!!errors.call_disposition}
                      isSearchable
                    />
                  </div>
                )}

                {isAdminOrStaff && (
                  <StatusSelect
                    id="status"
                    name="status"
                    placeholder="Status"
                    value={status}
                    stage={stage}
                    onChange={onStatusChange}
                    isInvalid={!!errors.status}
                    isSearchable
                  />
                )}

                {errors.status && (
                  <div className="alert alert-danger mt-2" role="alert">
                    {errors.status}
                  </div>
                )}
              </div>

              {showRatingSelect && (
                <div className="col-lg-4 pl0-md-down pr0-md-down mb10">
                  <Select
                    id="rating"
                    value={rating}
                    options={RATING_OPTIONS}
                    placeholder="Select Rating"
                    onChange={onRatingChange}
                    isSearchable
                  />
                </div>
              )}

              {showTaskSelect && isAdminOrStaff && (
                <div
                  className={classNames(
                    "col-lg-4 pl0 pr0 mb10 col-md-12",
                    !showRatingSelect && "ml-auto",
                  )}
                >
                  {this.renderTasksControls()}
                </div>
              )}
            </div>

            <Collapse in={isOpen} mountOnEnter unmountOnExit>
              <div className="mt20">{this.renderTasksForm()}</div>
            </Collapse>
          </>
        )}

        <div className="row mt20 ml0 mr0">
          <div className="col-sm-6 mb10 p0">
            {isAdminOrStaff &&
              (internal ? (
                <Tooltipable text="Shared with staff only">
                  <button
                    type="button"
                    id="visibility-button"
                    className="btn btn-secondary"
                    onClick={onToggleSharedType}
                  >
                    <FontAwesomeIcon
                      icon={["far", "fa-eye-slash"]}
                      className="mr5"
                    />{" "}
                    Staff Only
                  </button>
                </Tooltipable>
              ) : (
                <Tooltipable text="Shared with referring agent">
                  <button
                    type="button"
                    id="visibility-button"
                    className="btn btn-secondary"
                    onClick={onToggleSharedType}
                  >
                    <FontAwesomeIcon icon={["far", "fa-eye"]} className="mr5" />{" "}
                    Shared
                  </button>
                </Tooltipable>
              ))}
          </div>
          <div className="col-sm-6 text-right p0 mb10" ref={this.containerRef}>
            <div>
              {submitActionsComponent || (
                <>
                  {activityType === "direct_email" && (
                    <Tooltipable
                      text={emailTooltip}
                      trigger="click"
                      className="mr-2 text-secondary"
                    >
                      <FontAwesomeIcon
                        icon={["fal", "fa-info-circle"]}
                        size="lg"
                      />
                    </Tooltipable>
                  )}

                  <div
                    className="btn-group"
                    role="group"
                    aria-label="email-group"
                  >
                    {submitButtonText === "Send with Google" ? (
                      <>
                        <OverlayTrigger
                          trigger="click"
                          placement="bottom"
                          show={showPopover}
                          overlay={renderPopover}
                        >
                          <button
                            type="submit"
                            className={classNames(
                              "btn btn-primary btn-block-sm-down",
                              { disabled: sending },
                            )}
                            onClick={onSubmit}
                            disabled={disabledButton || sending}
                          >
                            {sending ? (
                              <span>
                                <FontAwesomeIcon
                                  icon="far fa-spinner"
                                  pulse
                                  className="mr5"
                                />{" "}
                                {submitButtonSendingText}
                              </span>
                            ) : (
                              submitButtonText
                            )}
                          </button>
                        </OverlayTrigger>

                        {showScheduleButton && (
                          <ScheduleButton
                            schedule={schedule}
                            setSchedule={setSchedule}
                            disabled={disabledButton || sending}
                            containerRef={this.containerRef}
                            popoverPlacement={scheduleBtnPopoverPlacement}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          type="submit"
                          className={classNames(
                            "btn btn-primary btn-block-sm-down",
                            { disabled: sending },
                          )}
                          onClick={onSubmit}
                          disabled={disabledButton || sending}
                        >
                          {sending ? (
                            <span>
                              <FontAwesomeIcon
                                icon="far fa-spinner"
                                pulse
                                className="mr5"
                              />{" "}
                              {submitButtonSendingText}
                            </span>
                          ) : (
                            submitButtonText
                          )}
                        </button>

                        {showScheduleButton && (
                          <ScheduleButton
                            schedule={schedule}
                            setSchedule={setSchedule}
                            disabled={disabledButton || sending}
                            containerRef={this.containerRef}
                            popoverPlacement={scheduleBtnPopoverPlacement}
                          />
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

FormFooterView.propTypes = {
  scheduleBtnPopoverPlacement: PropTypes.string,
  rating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  status: PropTypes.string,
  call_disposition: PropTypes.string,
  warningMessage: PropTypes.string,
  submitButtonText: PropTypes.string.isRequired,
  submitButtonSendingText: PropTypes.string.isRequired,
  lead: PropTypes.shape({}),
  task: PropTypes.shape({}),
  errors: PropTypes.shape({}),
  schedule: PropTypes.shape({}),
  submitActionsComponent: PropTypes.shape({}),
  internal: PropTypes.bool,
  sending: PropTypes.bool,
  showRatingSelect: PropTypes.bool,
  disabledButton: PropTypes.bool,
  showScheduleButton: PropTypes.bool,
  isEditingMode: PropTypes.bool,
  showPopover: PropTypes.bool,
  setSchedule: PropTypes.func,
  onToggleSharedType: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onCallDispositionChange: PropTypes.func.isRequired,
  setTaskField: PropTypes.func.isRequired,
  onRatingChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  renderPopover: PropTypes.func.isRequired,
};

FormFooterView.defaultProps = {
  scheduleBtnPopoverPlacement: "bottom",
  rating: "",
  status: "",
  call_disposition: "",
  lead: {},
  task: {},
  errors: {},
  schedule: {},
  warningMessage: "",
  showPopover: false,
  submitActionsComponent: null,
  internal: false,
  sending: false,
  showRatingSelect: false,
  disabledButton: false,
  showScheduleButton: false,
  isEditingMode: false,
  setSchedule: noop,
};

export default FormFooterView;
